import { useState, useCallback } from 'react';
import { useAppStore } from '@/hooks/useAppStore';
import { ImageConverter } from '@/lib/imageConverter';
import type { ConversionOptions } from '@/types';

export function Header() {
  const { files, globalFormat, globalQuality, setGlobalFormat, setGlobalQuality, applyGlobalSettings, clearFiles } = useAppStore();
  const [isConverting, setIsConverting] = useState(false);

  const handleConvertAll = useCallback(async () => {
    const pendingFiles = files.filter((f) => f.status === 'pending');
    if (pendingFiles.length === 0) return;

    setIsConverting(true);
    const converter = ImageConverter.getInstance();

    await Promise.all(
      pendingFiles.map(async (file) => {
        try {
          useAppStore.getState().updateFileStatus(file.id, 'converting');

          const options: ConversionOptions = {
            format: file.targetFormat,
            quality: file.quality,
            keepAspectRatio: true,
          };

          const result = await converter.convert(
            file.file,
            options,
            (progress) => {
              useAppStore.getState().updateFileStatus(file.id, 'converting', progress);
            }
          );

          useAppStore.getState().setFileResult(file.id, result);
        } catch (error) {
          useAppStore.getState().setFileError(
            file.id,
            error instanceof Error ? error.message : '转换失败'
          );
        }
      })
    );

    setIsConverting(false);
  }, [files]);

  const handleDownloadAll = useCallback(async () => {
    const completedFiles = files.filter((f) => f.status === 'completed' && f.result);
    if (completedFiles.length === 0) return;

    if (completedFiles.length === 1) {
      // 单文件直接下载
      const file = completedFiles[0];
      const link = document.createElement('a');
      link.href = file.result!.url;
      link.download = file.result!.name;
      link.click();
    } else {
      // 多文件使用 ZIP
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      completedFiles.forEach((file) => {
        zip.file(file.result!.name, file.result!.blob);
      });

      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      link.download = `picformat-${Date.now()}.zip`;
      link.click();
      URL.revokeObjectURL(url);
    }
  }, [files]);

  const completedCount = files.filter((f) => f.status === 'completed').length;
  const pendingCount = files.filter((f) => f.status === 'pending').length;

  return (
    <header className="sticky top-0 z-50 glass border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center">
              <svg className="w-5 h-5 text-background" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <span className="text-xl font-bold gradient-text">PicFormat</span>
          </div>

          {/* Global Settings */}
          {files.length > 0 && (
            <div className="hidden md:flex items-center gap-4">
              <select
                value={globalFormat}
                onChange={(e) => {
                  setGlobalFormat(e.target.value as any);
                  applyGlobalSettings();
                }}
                className="px-3 py-1.5 bg-surface border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-primary"
              >
                <option value="webp">WebP</option>
                <option value="png">PNG</option>
                <option value="jpg">JPEG</option>
                <option value="avif">AVIF</option>
                <option value="gif">GIF</option>
              </select>

              <div className="flex items-center gap-2">
                <span className="text-xs text-text-secondary">质量</span>
                <input
                  type="range"
                  min="1"
                  max="100"
                  value={globalQuality}
                  onChange={(e) => {
                    setGlobalQuality(Number(e.target.value));
                    applyGlobalSettings();
                  }}
                  className="w-24 accent-primary"
                />
                <span className="text-xs text-text-secondary w-8">{globalQuality}%</span>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3">
            {files.length > 0 && (
              <>
                <button
                  onClick={clearFiles}
                  className="px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary transition-colors"
                >
                  清空
                </button>

                {pendingCount > 0 && (
                  <button
                    onClick={handleConvertAll}
                    disabled={isConverting}
                    className="px-4 py-1.5 bg-primary hover:bg-primary-dark text-background font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isConverting ? '转换中...' : `转换全部 (${pendingCount})`}
                  </button>
                )}

                {completedCount > 0 && (
                  <button
                    onClick={handleDownloadAll}
                    className="px-4 py-1.5 bg-surface-highlight hover:bg-surface-hover border border-border text-text-primary font-medium rounded-lg transition-all"
                  >
                    下载全部 ({completedCount})
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}